import { capitalize } from 'lodash-es'
import { SocialNetwork } from '~/components/details/ProviderDescription.vue'
import {
  getCredibilityScoreValue,
  getFreemiumCredibilityMixpanelValueNames,
  getFreemiumLimitedMixpanelValueNames,
  getFreemiumStatusesMixpanelValueNames,
  getMxFormattedVariant,
  getTabName,
  isMobile
} from '~/lib/utils/utils'
import {
  FREEMIUM_CREDIBILITY,
  FREEMIUM_EXIT_POPUP,
  FREEMIUM_LIMITED,
  FREEMIUM_PRO_MODAL,
  FREEMIUM_STATUS,
  LOOP_GUEST_ACCOUNT,
  LOOP_TRIAL_EXTENSION,
  SIMILAR_CARD,
  VIDEO_RECORDER,
  DASHBOARD_APPLICATION_STRATEGY_HEALTH_BAR
} from '~/plugins/feature-flags/flags'
import * as props from '~/plugins/mixpanel/props'
import { Auth } from '~/types/auth'

/**
 * The commented out sources are only possible from other contexts.
 */
export type SubSource =
  | 'dashboard.details-page'
  | 'dashboard.activation-modal'
  | 'dashboard.easy-tab-apply-all'
  | 'dashboard.started-tab'
// | 'quick-apply.auto'
// | 'quick-apply.manual'

export type ApplicationRequirementSource =
    | 'dashboard.details-page'
    | 'dashboard.activation-modal'
    | 'dashboard.easy-tab-apply-all'
    | 'dashboard.started-tab'
    | 'quick-apply.auto'
    | 'quick-apply.manual'

export default {
  [props.TAB_NAME]: ({ route }) => getTabName(route),
  [props.REQUIREMENT_COUNT]: ({ scholarship }) => scholarship.requirementTotalCount,
  [props.APPLICATION_TYPE]: ({ $accessor }) => $accessor.applicationReview.review.applicationType,
  [props.SCHOLARSHIP_ID]: ({ scholarship }) => scholarship.scholarshipId,
  [props.IS_RECOMMENDED]: ({ $accessor, scholarship }) => $accessor.recommendation.isRecommended(scholarship),
  [props.REQUIREMENT_TYPE]: ({ scholarship, requirement }) => {
    if (Object.keys(requirement).length) { return requirement.type }
    if (Object.keys(scholarship).length) { return scholarship.requirementList }
    return ''
  },
  [props.CONFIDENCE_SCORE]: ({ scholarship }) => scholarship.confidenceScore,
  [props.APPLICATION_COUNT]: ({ scholarship }) => scholarship.applicationCount,
  [props.SCHOLARSHIP_EXPIRATION_DATE]: ({ scholarship }) => scholarship.expirationDate,
  [props.SCHOLARSHIP_AMOUNT]: ({ scholarship }) => scholarship.amount,
  [props.IS_SEEN]: ({ scholarship }) => scholarship.isSeen,
  [props.IS_EASY]: ({ scholarship }) => !!scholarship.isEasy,
  [props.CLICKED_FROM]: ({ route }) => route.params.tabName === 'submitted' ? route.params.cardId ? 'Details' : 'Card' : '',
  [props.SELECTED_CATEGORIES]: ({ store }) => store.categories.categoriesList,
  [props.SCHOLARSHIP_CATEGORY]: ({ scholarship }) => scholarship.categories && scholarship.categories.length ? scholarship.categories.map(cat => cat.title) : null,
  [props.SCHOLARSHIP_POINTS]: ({ $accessor, scholarship }) => {
    if (!$accessor.featureSet.isGame) { return }
    return scholarship && scholarship.points
  },
  [props.TOTAL_USER_POINTS]: ({ $accessor, scholarship }) => {
    if (!$accessor.featureSet.isGame) { return }
    if (!scholarship || $accessor.gameLevel.totalUserPoints === undefined) { return }
    return $accessor.gameLevel.totalUserPoints + scholarship.points
  },
  [props.PAYOUT_METHOD]: ({ scholarship }) => !scholarship.payoutMethod || !scholarship.payoutMethod.length ? 'Not stated' : scholarship.payoutMethod[0].title,
  [props.AWARD_COUNT]: ({ scholarship }) => scholarship.awards || 0,
  [props.DERIVED_STATUS]: ({ scholarship }) => scholarship.derivedStatus || '',
  [props.IS_OPTIONAL]: ({ requirement }) => Number(requirement.isOptional),
  [props.IS_SIMILAR]: ({ scholarship }) => !!scholarship.isSimilar,

  // default props what extends passed props with each track call
  [props.ACCOUNT]: ({ $auth }) => $auth.user.id,
  [props.IS_MEMBER]: ({ $accessor }) => $accessor.account.isMember,
  [props.AFF_ID]: ({ $auth }) => $auth.user.meta.affid || 'none',
  [props.FSET]: ({ $auth }) => $auth.user.fset?.name,
  [props.IS_MOBILE]: () => isMobile(),
  [props.SRV]: ({ $auth }) => $auth.user.meta.srv,
  [props.COUNTRY]: ({ $auth }) => $auth.user.meta.countryCodeByIp,
  [props.URL]: () => window.location.pathname,
  [props.SCHOLARSHIP_TYPE]: ({ scholarship }) => scholarship.scholarshipType || '',
  [props.MEMBERSHIP_STATUS]: ({ $auth }: { $auth: { user: Auth.User } }) => $auth.user.meta.membershipStatus,

  // user info update
  [props.SAVED_SCHOLARSHIP_COUNT]: store => store.getters['tabList/saved'].length,
  [props.IGNORED_SCHOLARSHIP_COUNT]: store => store.getters['tabList/ignored'].length,
  [props.STARTED_SCHOLARSHIP_COUNT]: store => store.getters['tabList/started'].length,
  [props.CAP_FSET]: store => store.state.auth.user.fset?.name,
  [props.LEVEL_NAME]: store => store.getters['gameLevel/currentLevelName'],
  [props.GOAL_SET]: store => store.getters['gameStreak/streak'],

  // package info
  [props.APPLICATION_SUBMITTED_COUNT]: store => Object.keys(store.state.cardList.applicationList).length,
  [props.ELIGIBLE_SCHOLARSHIP_COUNT]: store => Object.keys(store.state.cardList.scholarshipList).length,

  [props.MAINTENANCE_MODE]: ({ $accessor }) => $accessor.account.isMaintenanceMode,

  [props.MEMBERSHIP_NAME]: ({ $auth }) => $auth.user.membership?.package?.name,

  [props.FREEMIUM_LIMITED]: () => getFreemiumLimitedMixpanelValueNames(window.$nuxt.$featureFlags?.getVariant(FREEMIUM_LIMITED)?.name),

  [props.PRO_MODAL_NEW]: () => getMxFormattedVariant(window.$nuxt.$featureFlags?.getVariant?.(FREEMIUM_PRO_MODAL)?.name),

  [props.FREEMIUM_CREDIBILITY]: () => getFreemiumCredibilityMixpanelValueNames(window.$nuxt.$featureFlags?.getVariant(FREEMIUM_CREDIBILITY)?.name),

  [props.CREDIBILITY_SCORE]: ({ scholarship }) => getCredibilityScoreValue(scholarship.confidenceScore),

  [props.FREEMIUM_STATUSES]: () => getFreemiumStatusesMixpanelValueNames(window.$nuxt.$featureFlags?.getVariant(FREEMIUM_STATUS)?.name),

  [props.SIMILAR_CARD]: () => capitalize(window.$nuxt.$featureFlags?.getVariant(SIMILAR_CARD)?.name),

  [props.FREEMIUM_EXIT_POPUP]: () => getMxFormattedVariant(window.$nuxt.$featureFlags?.getVariant(FREEMIUM_EXIT_POPUP)?.name),

  [props.VIDEO_RECORDER]: () => window.$nuxt.$featureFlags?.isEnabled?.(VIDEO_RECORDER),

  [props.HEALTH_BAR_FEATURE_FLAG]: () => {
    const variant = window.$nuxt.$featureFlags?.getVariant?.(DASHBOARD_APPLICATION_STRATEGY_HEALTH_BAR)
    return variant?.enabled ? (variant.name === 'true' ? 'test' : variant.name) : 'Disabled'
  },

  [props.LOOP_GUEST_ACCOUNT]: () => {
    const variant = window.$nuxt.$featureFlags?.getVariant?.(LOOP_GUEST_ACCOUNT)
    return variant?.enabled ? variant.name : 'Disabled'
  },

  [props.LOOP_TRIAL_EXTENSION]: () => {
    const variant = window.$nuxt.$featureFlags?.getVariant?.(LOOP_TRIAL_EXTENSION)
    return variant?.enabled ? variant.name : 'Disabled'
  },

  [props.SOCIAL_NETWORK_NAME]: (name: keyof typeof SocialNetwork) => SocialNetwork[name]
}
